import React, { useState, useEffect } from 'react';
import { 
  Flag, 
  Filter, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Eye,
  RefreshCw,
  FileText,
  MessageSquare,
  User,
  ChevronRight,
  Shield,
  Info,
  ChevronDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useReportStore from '../../store/reportStore';
import { formatDistanceToNow } from 'date-fns';

const UserReportsPage = () => {
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const navigate = useNavigate();

  const {
    userReports,
    userReportsPagination,
    isLoading: isStoreLoading,
    getUserReports
  } = useReportStore();

  useEffect(() => {
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    setIsLoading(true);
    try {
      await getUserReports({ 
        status: filter === 'all' ? 'all' : filter,
        page: 1,
        limit: 20 
      });
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
      case 'REVIEWING':
        return <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
      case 'RESOLVED':
        return <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
      case 'DISMISSED':
        return <XCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
      default:
        return <Flag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'REVIEWING':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'RESOLVED':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'DISMISSED':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100';
      case 'REVIEWING':
        return 'bg-blue-100';
      case 'RESOLVED':
        return 'bg-green-100';
      case 'DISMISSED':
        return 'bg-gray-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getReportTypeIcon = (report) => {
    if (report.postId) return <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
    if (report.commentId) return <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
    if (report.userId) return <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
    return <Flag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
  };

  const getReportTypeLabel = (report) => {
    if (report.postId) return 'Post';
    if (report.commentId) return 'Comment';
    if (report.userId) return 'User';
    return 'Unknown';
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  const filters = [
    { id: 'all', label: 'All', icon: Flag, color: 'gray' },
    { id: 'PENDING', label: 'Pending', icon: Clock, color: 'yellow' },
    { id: 'REVIEWING', label: 'Reviewing', icon: AlertCircle, color: 'blue' },
    { id: 'RESOLVED', label: 'Resolved', icon: CheckCircle, color: 'green' },
    { id: 'DISMISSED', label: 'Dismissed', icon: XCircle, color: 'gray' },
  ];

  const isLoadingData = isLoading || isStoreLoading;

  // Mobile Filter Modal
  const MobileFiltersModal = () => (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-neomorph max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-primary-100/50 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Filter Reports</h3>
          <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronDown className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {filters.map((filterItem) => (
            <button
              key={filterItem.id}
              onClick={() => {
                setFilter(filterItem.id);
                setShowMobileFilters(false);
              }}
              className={`w-full flex items-center justify-between p-4 rounded-lg transition-all ${
                filter === filterItem.id
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'bg-gray-50 text-gray-600 border border-transparent'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  filter === filterItem.id ? 'bg-primary-100' : 'bg-white'
                }`}>
                  <filterItem.icon className={`h-4 w-4 ${
                    filter === filterItem.id ? 'text-primary-600' : 'text-gray-400'
                  }`} />
                </div>
                <span className="font-medium">{filterItem.label}</span>
              </div>
              {filter === filterItem.id && <CheckCircle className="h-5 w-5 text-primary-600" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto py-4 sm:py-8 px-3 sm:px-4">
      {/* Header */}
      <div className="bg-[#E8ECF5] rounded-xl p-4 sm:p-5 shadow-neomorph border border-primary-100/50 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center">
            <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2 sm:mr-3" />
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800">My Reports</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Track your submitted reports</p>
            </div>
          </div>
          
          <button
            onClick={loadReports}
            disabled={isLoadingData}
            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center justify-center disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 sm:mr-2 ${isLoadingData ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Sync</span>
          </button>
        </div>
      </div>

      {/* Filter Tabs - Desktop */}
      <div className="hidden sm:block bg-white rounded-xl shadow-neomorph p-4 border border-primary-100/30 mb-6">
        <div className="flex flex-wrap gap-2">
          {filters.map((filterItem) => (
            <button
              key={filterItem.id}
              onClick={() => setFilter(filterItem.id)}
              className={`flex items-center px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                filter === filterItem.id
                  ? 'bg-primary-50 text-primary-700 shadow-neomorph-inset border border-primary-200'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
            >
              <div className={`p-1 rounded-md mr-1.5 sm:mr-2 ${
                filter === filterItem.id ? 'bg-primary-100' : 'bg-gray-200'
              }`}>
                <filterItem.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${
                  filter === filterItem.id ? 'text-primary-600' : 'text-gray-500'
                }`} />
              </div>
              {filterItem.label}
              <span className={`ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full ${
                filter === filterItem.id 
                  ? 'bg-primary-100 text-primary-700' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {userReports.filter(r => filterItem.id === 'all' || r.status === filterItem.id).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Filter Button */}
      <button
        onClick={() => setShowMobileFilters(true)}
        className="sm:hidden w-full mb-4 py-2.5 bg-white rounded-xl shadow-neomorph flex items-center justify-center gap-2"
      >
        <Filter className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">Filter Reports</span>
        <span className="ml-auto px-2 py-0.5 bg-primary-50 text-primary-600 text-xs rounded-full">
          {userReports.filter(r => filter === 'all' || r.status === filter).length}
        </span>
      </button>

      {/* Reports List */}
      <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
        {isLoadingData && userReports.length === 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 bg-gray-50 rounded-lg animate-pulse">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : userReports.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {userReports.map((report) => (
              <div
                key={report.id}
                className="border border-primary-100/50 rounded-xl p-3 sm:p-5 hover:shadow-neomorph-hover transition-all bg-white"
              >
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl ${getStatusBg(report.status)} flex items-center justify-center shadow-neomorph-inset flex-shrink-0`}>
                      {getStatusIcon(report.status)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                        <h3 className="font-semibold text-gray-800 text-sm sm:text-base break-words">
                          {report.reason}
                        </h3>
                        <span className={`px-1.5 sm:px-2.5 py-0.5 text-[10px] sm:text-xs font-medium rounded-lg border ${getStatusColor(report.status)}`}>
                          {report.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500">
                        <span className="flex items-center bg-gray-100 px-1.5 sm:px-2 py-0.5 rounded-lg">
                          {getReportTypeIcon(report)}
                          <span className="ml-0.5 sm:ml-1">{getReportTypeLabel(report)}</span>
                        </span>
                        <span className="text-gray-300">•</span>
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-0.5 sm:mr-1" />
                          {formatTime(report.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {report.resolution && (
                    <button
                      onClick={() => alert(`Resolution: ${report.resolution}`)}
                      className="self-start p-1.5 sm:p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
                      title="View resolution"
                    >
                      <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
                    </button>
                  )}
                </div>

                {/* Report Details */}
                <div className="mt-2 sm:mt-3 pl-10 sm:pl-16">
                  {report.description && (
                    <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 p-2 sm:p-3 bg-gray-50 rounded-lg break-words">
                      {report.description}
                    </p>
                  )}

                  {/* Report Target Preview */}
                  <div className="p-2 sm:p-4 bg-gray-50 rounded-xl shadow-neomorph-inset">
                    {report.post && (
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] sm:text-xs font-medium text-gray-700 mb-1 flex items-center">
                            <FileText className="h-3 w-3 mr-1" />
                            Reported Post:
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 break-words">
                            {report.post.content}
                          </p>
                        </div>
                        <button
                          onClick={() => navigate(`/community/post/${report.post.id}`)}
                          className="px-2 sm:px-3 py-1 sm:py-1.5 bg-primary-50 text-primary-600 text-[10px] sm:text-xs font-medium rounded-lg hover:bg-primary-100 transition-all flex items-center justify-center whitespace-nowrap"
                        >
                          View Post
                          <ChevronRight className="h-3 w-3 ml-1" />
                        </button>
                      </div>
                    )}
                    
                    {report.comment && (
                      <div>
                        <p className="text-[10px] sm:text-xs font-medium text-gray-700 mb-1 flex items-center">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Reported Comment:
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 break-words">
                          {report.comment.content}
                        </p>
                      </div>
                    )}
                    
                    {report.reportedUser && (
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center flex-shrink-0">
                            {report.reportedUser.avatar ? (
                              <img 
                                src={report.reportedUser.avatar} 
                                alt={report.reportedUser.firstName}
                                className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover"
                              />
                            ) : (
                              <span className="text-white font-semibold text-xs sm:text-sm">
                                {report.reportedUser.firstName?.[0]}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs sm:text-sm font-medium text-gray-800 break-words">
                              {report.reportedUser.firstName} {report.reportedUser.lastName}
                            </p>
                            <button
                              onClick={() => navigate(`/community/profile/${report.reportedUser.id}`)}
                              className="text-[10px] sm:text-xs text-primary-600 hover:text-primary-800 font-medium"
                            >
                              View Profile →
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Resolution Details */}
                  {report.resolution && (
                    <div className="mt-2 sm:mt-3 p-2 sm:p-4 bg-green-50 rounded-xl border border-green-200 shadow-neomorph-inset">
                      <div className="flex items-start">
                        <div className="p-1 bg-green-100 rounded-lg mr-2 sm:mr-3 flex-shrink-0">
                          <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-sm font-medium text-green-800">Resolution</p>
                          <p className="text-xs sm:text-sm text-green-700 mt-0.5 sm:mt-1 break-words">{report.resolution}</p>
                          {report.resolvedAt && (
                            <p className="text-[10px] sm:text-xs text-green-600 mt-1 flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              Resolved {formatTime(report.resolvedAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Load More Button */}
            {userReportsPagination && userReportsPagination.totalPages > userReportsPagination.page && (
              <div className="text-center pt-2 sm:pt-4">
                <button
                  onClick={() => getUserReports({ 
                    status: filter === 'all' ? 'all' : filter,
                    page: userReportsPagination.page + 1 
                  })}
                  disabled={isLoadingData}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all disabled:opacity-50 text-sm"
                >
                  {isLoadingData ? 'Loading...' : 'Load More Reports'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-50 shadow-neomorph-inset mb-3 sm:mb-4">
              <Flag className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">
              No reports submitted
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 max-w-sm mx-auto px-4">
              You haven't submitted any reports yet. Reports help us maintain a safe community.
            </p>
            <button
              onClick={() => navigate('/community')}
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
            >
              Go to Community
            </button>
          </div>
        )}
      </div>

      {/* Report Guidelines */}
      <div className="bg-blue-50 rounded-xl shadow-neomorph-inset p-4 sm:p-5 mt-4 sm:mt-6 border border-blue-200">
        <div className="flex items-start">
          <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg mr-2 sm:mr-3 flex-shrink-0">
            <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-1.5 sm:mb-2">Report Guidelines</h4>
            <ul className="text-[10px] sm:text-xs text-blue-700 space-y-0.5 sm:space-y-1">
              <li className="flex items-start">
                <span className="mr-1.5 sm:mr-2">•</span>
                <span>Reports are reviewed within 24-48 hours</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1.5 sm:mr-2">•</span>
                <span>You will be notified when action is taken</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1.5 sm:mr-2">•</span>
                <span>False reports may result in account restrictions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1.5 sm:mr-2">•</span>
                <span>All reports are confidential and anonymous</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {showMobileFilters && <MobileFiltersModal />}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default UserReportsPage;